import React from "react";
import { Stack, Text, Icon, TooltipHost } from "@fluentui/react";
import { GaugeChart, GaugeChartVariant, GaugeValueFormat, Sparkline } from "@fluentui/react-charting";
import { CUSTOMER_HEALTH_STATUS_COLORS, IntuneCustomerHealthTooltips } from "./CustomerHealthView.constants";

interface IIntuneGaugeSectionProps {
  score: number;
  status: string;
}

export const IntuneGaugeSection: React.FC<IIntuneGaugeSectionProps> = ({ score, status }) => {
  // Dummy trend data for the sparkline
  const trendData = [68, 70, 65, 72, 71, 72];

  // Determine color based on score
  const getScoreColor = (score: number) => {
    if (score >= 71) return CUSTOMER_HEALTH_STATUS_COLORS.Healthy;
    if (score >= 41) return CUSTOMER_HEALTH_STATUS_COLORS.Warning;
    return CUSTOMER_HEALTH_STATUS_COLORS.Critical;
  };

  const scoreColor = getScoreColor(score);

  const latest = trendData[trendData.length - 1];
  const previous = trendData.length >= 2 ? trendData[trendData.length - 2] : latest;
  const delta = latest - previous;
  const deltaColor = delta > 0
    ? CUSTOMER_HEALTH_STATUS_COLORS.Healthy
    : delta < 0
      ? CUSTOMER_HEALTH_STATUS_COLORS.Critical
      : CUSTOMER_HEALTH_STATUS_COLORS.Warning;

  return (
    <Stack
      tokens={{ childrenGap: 8 }}
      styles={{
        root: {
          padding: "16px",
          border: "1px solid #EDEBE9",
          borderRadius: 8,
          backgroundColor: "#FFFFFF"
        }
      }}
    >
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>
          Intune
        </Text>
        <TooltipHost content={IntuneCustomerHealthTooltips.ScoreGauge}>
          <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
        </TooltipHost>
      </Stack>

      {/* Gauge Chart */}
      <Stack horizontalAlign="center" tokens={{ childrenGap: 6 }}>
        <GaugeChart
          width={240}
          height={160}
          chartValue={score}
          chartValueFormat={GaugeValueFormat.Fraction}
          hideMinMax={true}
          variant={GaugeChartVariant.MultipleSegments}
          roundCorners={true}
          enableGradient={false}
          hideLegend={true}
          segments={[
            {
              size: score,
              color: scoreColor,
              legend: "Score"
            },
            {
              size: Math.max(0, 100 - score),
              color: "#F3F2F1",
              legend: "Remaining"
            }
          ]}
        />
        <Text variant="xLarge" styles={{ root: { fontWeight: 700, color: scoreColor } }}>
          {status}
        </Text>
      </Stack>

      {/* Trend Sparkline */}
      <Stack tokens={{ childrenGap: 4 }} styles={{ root: { marginTop: 8 } }}>
        <Sparkline
          data={{
            chartTitle: "Intune Trend",
            lineChartData: [
              {
                legend: "Intune Score Trend",
                color: scoreColor,
                data: trendData.map((value, index) => ({ x: index + 1, y: value }))
              }
            ]
          }}
          width={240}
          height={40}
          showLegend={false}
        />
        <Stack horizontalAlign="center" tokens={{ childrenGap: 2 }}>
          <Text
            variant="large"
            styles={{
              root: {
                fontWeight: 600,
                whiteSpace: "nowrap",
                textAlign: "center",
                color: deltaColor,
              },
            }}
          >
            {delta > 0
              ? `▲ +${delta} pts`
              : delta < 0
                ? `▼ ${delta} pts`
                : "No change"}
          </Text>
          <Text variant="small" styles={{ root: { color: "#605E5C", textAlign: "center" } }}>vs prev. month</Text>
        </Stack>
      </Stack>
    </Stack>
  );
};
